import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "./common/ReportTable";
import TableSkeleton from "../utils/TableSkeleton";
import EmptyTableState from "../utils/EmptyTableState";
import * as Constants from "../utils/Constants";
import "../styles/components/reports.css";

import {formatDateToDDMMYYYY, getAuthToken, getClientIdOfUser, getGeolocationFromLatLng,} from "../utils/utility";

import {toLocalISO} from "../utils/dateUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import ReportAssetFilter from "./ReportAssetFilter";
import {customStyles} from "../utils/DataTableCustomStyle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBatteryHalf,
    faBolt,
    faBuilding,
    faCircleInfo,
    faClock,
    faCompass,
    faCubes,
    faFileExcel,
    faFilePdf,
    faHashtag,
    faImage,
    faKey,
    faLocationDot,
    faLock,
    faSignal,
    faTableList,
    faTag,
    faUnlock,
    faWarning,
} from "@fortawesome/free-solid-svg-icons";

const LockUnlockReportSearch = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const ExpandedComponent = ({ data }) => {
    const formatLabel = (key) =>
      key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

    const getIcon = (key) => {
      const k = key.toLowerCase();
      if (k === "reportid" || k === "id") return faHashtag;
      if (k.includes("assetid") || k.includes("deviceid")) return faCubes;
      if (k.includes("name")) return faTag;
      if (k.includes("unlock") || k.includes("lock")) return faLock;
      if (k.includes("password")) return faKey;
      if (k.includes("event")) return faBolt;
      if (k.includes("time") || k.includes("date")) return faClock;
      if (k.includes("location") || k.includes("address")) return faLocationDot;
      if (k.includes("lat") || k.includes("lon") || k.includes("lng")) return faCompass;
      if (k.includes("image") || k.includes("img")) return faImage;
      if (k.includes("client")) return faBuilding;
      if (k.includes("battery")) return faBatteryHalf;
      if (k.includes("signal") || k.includes("gsm")) return faSignal;
      return faCircleInfo;
    };

    const getIconStyle = (key) => {
      const k = key.toLowerCase();
      if (k.includes("lock") || k.includes("unlock")) return { background: "#fff1f2", color: "#ff683a" };
      if (k.includes("time") || k.includes("date")) return { background: "#eff6ff", color: "#3b82f6" };
      if (k.includes("location") || k.includes("lat") || k.includes("lon")) return { background: "#f0fdf4", color: "#10b981" };
      if (k.includes("event")) return { background: "#fff7ed", color: "#f59e0b" };
      if (k.includes("password") || k === "reportid" || k === "id") return { background: "#f5f3ff", color: "#7c3aed" };
      if (k.includes("name") || k.includes("asset")) return { background: "#fef9ee", color: "#d97706" };
      return { background: "var(--color-primary-ghost)", color: "var(--color-primary)" };
    };

    const formatValue = (key, value) => {
      if (key === "reportDateTime") return toLocalISO(value) || "—";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (value === null || value === undefined || value === "") return "—";
      return String(value);
    };

    return (
      <div className="expanded-row-content">
        <div className="expanded-row-header">
          <FontAwesomeIcon icon={faTableList} />
          Full Record Details
        </div>
        <div className="expanded-grid">
          {Object.entries(data).map(([key, value]) => {
            const iconStyle = getIconStyle(key);
            return (
              <div key={key} className="expanded-item">
                <div className="expanded-item-icon" style={iconStyle}>
                  <FontAwesomeIcon icon={getIcon(key)} />
                </div>
                <div className="expanded-item-body">
                  <div className="expanded-item-label">{formatLabel(key)}</div>
                  <div className="expanded-item-value">{formatValue(key, value)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- COLUMNS: ids get room to render in full, free-text columns flex,
  //     the address renders smaller and wraps (no truncation anywhere) ---
  const columns = [
    {
      name: "Report Id", id: "Report Id",
      selector: (row) => row.reportId,
      sortable: true,
      width: "84px",
    },
    {
      name: "Asset Id", id: "Asset Id",
      selector: (row) => row.assetId,
      sortable: true,
      width: "118px", // full 10-digit unit ids, no ellipsis
    },
    {
      name: "Asset Name", id: "Asset Name",
      selector: (row) => row.assetName,
      sortable: true,
      grow: 1,
      minWidth: "120px",
      wrap: true,
    },
    {
      name: "Lock Status", id: "Lock Status",
      selector: (row) => row.unlockSuccessful,
      sortable: true,
      width: "118px",
      cell: (row) => {
        const isUnlocked = row.unlockSuccessful;

        const eventSrc = (row.eventSource || "").toLowerCase();
        const showWarning =
          row.passwordCorrect === false &&
          !isUnlocked &&
          (eventSrc.includes("unlock") || eventSrc.includes("authorized rfid"));

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span className={`lock-status-badge ${isUnlocked ? "unlocked" : "locked"}`}>
              <FontAwesomeIcon icon={isUnlocked ? faUnlock : faLock} />
              {isUnlocked ? "Unlocked" : "Locked"}
            </span>
            {showWarning && (
              <span
                style={{ fontSize: "14px", cursor: "help" }}
                title="Incorrect Password"
              >
                <FontAwesomeIcon
                  icon={faWarning}
                  beatFade
                  style={{
                    color: "#FFD43B",
                    filter: "drop-shadow(0 0 2px #000)",
                  }}
                />
              </span>
            )}
          </div>
        );
      },
    },
    {
      name: "Event", id: "Event",
      selector: (row) => row.eventSource || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "150px",
      wrap: true,
    },
    {
      name: "Date-Time", id: "Date-Time",
      selector: (row) => row.reportDateTime,
      sortable: true,
      width: "118px", // wraps as date / time on two short lines
      wrap: true,
      cell: (row) =>
        row.reportDateTime ? toLocalISO(row.reportDateTime) : "N/A",
    },
    {
      name: "Location", id: "Location",
      selector: (row) => row.location,
      wrap: true,
      grow: 1.8,
      minWidth: "240px",
      style: { fontSize: "12px", lineHeight: "1.45", color: "#475569" },
      format: (row) => `${row.location ? row.location : ""}`,
    },
  ];

  // Called by Search button — validates filters and resets to page 1
  const onSearchClick = () => {
    if (Object.keys(checkedItems).filter((id) => checkedItems[id]).length === 0 && (!startDate || !endDate)) {
      alert("Select Asset and/or Date Range.");
      return;
    }
    setCurrentPage(1);
    handleSearch(1, perPage);
  };

  const handleSearch = async (page = 1, size = perPage) => {
    setIsLoading(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const checkedAssetIds = Object.keys(checkedItems).filter((id) => checkedItems[id]);

      const params = new URLSearchParams();
      if (checkedAssetIds.length > 0) params.set("assetIds", checkedAssetIds.join(","));
      if (startDate && endDate) {
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      params.set("timezone", timezone);
      params.set("page", page - 1); // Spring is 0-indexed, DataTable is 1-indexed
      params.set("size", size);

      const apiUrl = `api/v1/${getClientIdOfUser()}/lock-unlock-reports?${params.toString()}`;
      const response = await axios.get(Constants.BASE_URL + apiUrl);

      if (response.status === 200) {
        const pageData = response.data;
        let data = pageData.content || [];
        data = await updateLocationForReports(data);
        data = updateAssetNameInReport(assetList, data);
        setReports(data);
        setTotalRows(pageData.totalElements || 0);
      } else if (response.status === 403) {
        handleLogout();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    handleSearch(page, perPage);
  };

  const handlePerRowsChange = (newSize, page) => {
    setPerPage(newSize);
    setCurrentPage(page);
    handleSearch(page, newSize);
  };

  const updateAssetNameInReport = (assets, reports) => {
    return reports.map((r) => {
      const asset = assets.find((a) => a.assetUnitId === r.assetId);
      return { ...r, assetName: asset ? asset.name : "-" };
    });
  };

  async function updateLocationForReports(reports) {
    return Promise.all(
      reports.map(async (report) => {
        if (report.latitude && report.longitude) {
          const loc = await getGeolocationFromLatLng(
            report.latitude,
            report.longitude
          );
          return { ...report, location: loc || "Location not available" };
        }
        return report;
      })
    );
  }

  const getUserRole = () => {
    return localStorage.getItem("role");
  };

  const getUserId = () => {
    const loggedInUser = JSON.parse(
      localStorage.getItem(Constants.LOGGED_IN_USER)
    );
    return loggedInUser?.userId;
  };

  const fetchAssignedAssetsForUser = async (userId) => {
    try {
      const endpoint = `${Constants.API_V1
        }/${getClientIdOfUser()}/asset-management/user/${userId}`;
      axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;

      const response = await axios.get(`${Constants.BASE_URL}${endpoint}`);
      const raw = Array.isArray(response.data) ? response.data : [];

      const assignedAssetIds = raw
        .filter((a) => !a.returnDate)
        .map((a) => a.asset?.assetId)
        .filter(Boolean);

      return assignedAssetIds;
    } catch (err) {
      console.error("Error fetching assigned assets:", err);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL
        }`;
      axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
      const res = await axios.get(`${Constants.BASE_URL}${endPoint}`);

      if (res.status === 200) {
        let allAssets = res.data.responseData || [];

        // Filter only GPS_LOCK assets
        const gps_lockAssets = allAssets.filter(
          (asset) => (asset.assetType || "").toUpperCase() === "GPS_LOCK"
        );

        const userRole = getUserRole();
        const userId = getUserId();

        // If user is CLIENT_ADMIN or CLIENT_MANAGER, return all GPS_LOCK assets
        if (userRole === "CLIENT_ADMIN" || userRole === "CLIENT_MANAGER") {
          return gps_lockAssets;
        }

        // For other roles, fetch assigned assets and filter
        const assignedAssetIds = await fetchAssignedAssetsForUser(userId);

        const filteredAssets = gps_lockAssets.filter((asset) =>
          assignedAssetIds.includes(asset.assetId)
        );

        return filteredAssets;
      }
    } catch (err) {
      console.error("Error fetching assets:", err);
      handleLogout();
    }
    return [];
  };

  const exportToPDF = () => {
    // 1. Setup Document
    const doc = new jsPDF("l", "pt", "a4");
    const margin = 30;

    // --- DATA PREPARATION ---
    const tableData = reports.map((r) => {
      let statusText = r.unlockSuccessful ? "Unlocked" : "Locked";

      const eventSrc = (r.eventSource || "").toLowerCase();
      const showWarning =
        r.passwordCorrect === false &&
        !r.unlockSuccessful &&
        (eventSrc.includes("unlock") || eventSrc.includes("authorized rfid"));

      if (showWarning) {
        statusText += "\n(Wrong Password)";
      }

      return [
        r.reportId || "-",
        r.assetId || "-",
        r.assetName || "-",
        statusText,
        r.eventSource || "-",
        toLocalISO(r.reportDateTime) || "-",
        (r.location || "").substring(0, 100),
      ];
    });

    // --- TABLE GENERATION ---
    autoTable(doc, {
      startY: 85, // Leave space for header
      head: [
        [
          "Report ID",
          "Asset ID",
          "Name",
          "Status",
          "Event",
          "Date-Time",
          "Location",
        ],
      ],
      body: tableData,
      theme: "grid",

      styles: {
        fontSize: 9,
        cellPadding: 8,
        textColor: [51, 65, 85],
        lineColor: [226, 232, 240],
        lineWidth: 0.5,
        valign: "middle",
        overflow: "linebreak",
      },

      headStyles: {
        fillColor: [54, 49, 104], // Yekkada Navy
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        halign: "left",
      },

      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },

      columnStyles: {
        0: { cellWidth: 50, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 90 },
        3: { cellWidth: 90 },
        4: { cellWidth: 90 },
        5: { cellWidth: 100 },
        6: { cellWidth: "auto" },
      },

      margin: { top: 85, left: margin, right: margin, bottom: 50 },

      // --- PAGE DRAWING HOOK (Runs for EVERY Page) ---
      didDrawPage: function (data) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // 1. DRAW HEADER
        try {
          const img = new Image();
          img.src = require("../assets/yekkada-logo-new.png");
          // Logo: Top Left
          doc.addImage(img, "PNG", margin, 20, 120, 42);
        } catch {
          // ignore if missing
        }

        // Title: Top Right (Reduced Font Size to 14)
        doc.setFontSize(12);
        doc.setTextColor(54, 49, 104);
        doc.setFont("helvetica", "normal");
        const title = "GPS Lock/Unlock Reports";
        const titleWidth = doc.getTextWidth(title);
        doc.text(title, pageWidth - margin - titleWidth, 55);

        // Header Divider Line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(1);
        doc.line(margin, 60, pageWidth - margin, 60);

        // 2. DRAW FOOTER
        const footerY = pageHeight - 30; // Position for footer text
        const footerLineY = pageHeight - 45; // Position for footer line

        // Footer Divider Line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(1);
        doc.line(margin, footerLineY, pageWidth - margin, footerLineY);

        // Footer Text
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);

        const pageNum = "Page " + doc.internal.getNumberOfPages();
        const dateStr = "Generated: " + new Date().toLocaleString();

        // Page Num (Left)
        doc.text(pageNum, margin, footerY);

        // Date (Right)
        const dateWidth = doc.getTextWidth(dateStr);
        doc.text(dateStr, pageWidth - margin - dateWidth, footerY);
      },
    });

    const now = new Date();
    const timestamp =
      now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, "0") +
      now.getDate().toString().padStart(2, "0") +
      "_" +
      now.getHours().toString().padStart(2, "0") +
      now.getMinutes().toString().padStart(2, "0") +
      now.getSeconds().toString().padStart(2, "0");

    doc.save(`gps_lock_unlock_reports_${timestamp}.pdf`);

    doc.save("gps_lock_unlock_reports.pdf");
  };

  const exportToExcel = () => {
    // 1. Prepare Data with Logic
    const excelData = reports.map((r) => {
      // Base Status
      let statusText = r.unlockSuccessful ? "Unlocked" : "Locked";

      // Warning Logic (Same as Table/PDF)
      const eventSrc = (r.eventSource || "").toLowerCase();
      const showWarning =
        r.passwordCorrect === false &&
        !r.unlockSuccessful &&
        (eventSrc.includes("unlock") || eventSrc.includes("authorized rfid"));

      if (showWarning) {
        statusText += " (Wrong Password)";
      }

      // Return clean object for Excel
      return {
        "Report ID": r.reportId || "-",
        "Asset ID": r.assetId || "-",
        "Asset Name": r.assetName || "-",
        Status: statusText, // Updated Status
        Event: r.eventSource || "-",
        "Date-Time": r.reportDateTime ? toLocalISO(r.reportDateTime) : "-",
        Location: r.location || "-",
      };
    });

    // 2. Create Workbook
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Optional: Adjust column widths for better readability in Excel
    const wscols = [
      { wch: 10 }, // Report ID
      { wch: 15 }, // Asset ID
      { wch: 20 }, // Asset Name
      { wch: 25 }, // Status (wider for warning text)
      { wch: 25 }, // Event
      { wch: 22 }, // Date-Time
      { wch: 50 }, // Location
    ];
    ws["!cols"] = wscols;

    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // 3. Generate Timestamp
    const now = new Date();
    const timestamp =
      now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, "0") +
      now.getDate().toString().padStart(2, "0") +
      "_" +
      now.getHours().toString().padStart(2, "0") +
      now.getMinutes().toString().padStart(2, "0") +
      now.getSeconds().toString().padStart(2, "0");

    // 4. Save File
    FileSaver.saveAs(
      new Blob([excelBuffer]),
      `gps_lock_unlock_reports_${timestamp}.xlsx`
    );
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setReports([]);
    setCheckedItems({});
    setTotalRows(0);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchData().then((data) => setAssetList(data || []));
  }, []);

  const paginationComponentOptions = {
    rowsPerPageText: "Rows per page:",
    rangeSeparatorText: "of",
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            GPS Lock Unlock Results{" "}
            {totalRows > 0 && `(${totalRows})`}
          </span>
          {totalRows > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportToExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportToPDF}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        <div className="report-table-wrapper">
          <ReportTable
            columns={columns}
            data={reports}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationRowsPerPageOptions={[25, 50, 100]}
            paginationComponentOptions={paginationComponentOptions}
            customStyles={customStyles}
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            progressPending={isLoading}
            progressComponent={<TableSkeleton />}
            fixedHeader={true}
            fixedHeaderScrollHeight="100%"
            noDataComponent={<EmptyTableState variant="search" />}
          />
        </div>
      </div>

      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>

        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">Select Assets</label>
            <ReportAssetFilter
              assets={assetList}
              checkedItems={checkedItems}
              setCheckedItems={setCheckedItems}
            />
          </div>
          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) =>
                setStartDate(formatDateToDDMMYYYY(e.target.value))
              }
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
            />
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearchClick}>
              {Constants.FETCH_REPORTS}
            </button>
            <button className="btn-report secondary" onClick={handleClear}>
              {Constants.CLEAR_FILTERS}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LockUnlockReportSearch;
